import { DateTime } from "luxon";
import {
  LifecycleCycle,
  LifecycleEvent,
  LifecycleGroup,
} from "./DataServices/EventHistoryDataService";

export type FlowKind = "Renewal" | "MoveOut" | "Rental";

/**
 * Touring is not a backend milestone. It is derived from the confirmed slot carried by
 * Rental.TourBooked, so it turns active on its own once the slot time arrives.
 */
export const TOURING_STEP = "__touring";

export interface StepTemplate {
  key: string;
  label: string;
  description: string;
  /** Rental only: the milestone exists only under this workflow variant. */
  workflow?: "Mandatory" | "Optional";
}

export interface TimelineFact {
  label: string;
  value: string;
  strong?: boolean;
}

export interface TimelineStep {
  key: string;
  label: string;
  description: string;
  /** Rental only: this milestone belongs to one workflow variant — shown as a badge either way. */
  workflow?: "Mandatory" | "Optional";
  /** An event for this milestone exists in the cycle. */
  active: boolean;
  /** The most recent active step of the flow. */
  isLatest: boolean;
  occurredAt?: string;
  /** False only when the backend marked this date a sync-time stand-in, not a real extracted one. */
  dateExact: boolean;
  statusText: string;
  facts: TimelineFact[];
}

export const FLOW_LABELS: Record<FlowKind, string> = {
  Renewal: "Renewal Flow",
  MoveOut: "Move-out Flow",
  Rental: "Rental Flow",
};

const RENEWAL_STEPS: StepTemplate[] = [
  {
    key: "UpForRenewal",
    label: "Up for Renewal",
    description: "The lease is approaching its end date and is eligible for an offer.",
  },
  {
    key: "OfferSent",
    label: "Renewal Email Sent",
    description: "Renewal terms and the proposed rent were emailed to the tenant group.",
  },
  {
    key: "PartiallySigned",
    label: "Partially Signed",
    description: "Some members have signed while the remaining signatures are still pending.",
  },
  {
    key: "Renewed",
    label: "Renewed",
    description: "Every member signed and the tenancy was extended for a new term.",
  },
  {
    key: "NotRenewing",
    label: "Not Renewing",
    description: "The tenant declined the offer, so the unit will be prepared for a new listing.",
  },
  {
    key: "BeingMarketed",
    label: "Being Marketed",
    description: "The unit is listed and actively advertised to prospective renters.",
  },
];

const MOVEOUT_STEPS: StepTemplate[] = [
  {
    key: "GeneralInstructionSent",
    label: "General Instruction Email Sent",
    description: "Move-out instructions and showing access details were emailed to the tenants.",
  },
  {
    key: "Initiated",
    label: "Deposit Receiver Email Sent",
    description:
      "The security deposit receiver selection email was sent, asking the tenants who should receive the returned deposit.",
  },
  {
    key: "ReceiverSelected",
    label: "Receiver Vote Received",
    description: "Each tenant votes on who should receive the returned security deposit.",
  },
  {
    key: "ReceiverConsensusReached",
    label: "Receiver Consensus Reached",
    description: "The group agreed on a single deposit receiver.",
  },
  {
    key: "DepositPdfSent",
    label: "Security Deposit PDF Sent",
    description: "A signed deposit summary was generated and sent for the tenants' records.",
  },
  {
    key: "BankDetailsRequested",
    label: "Bank Details Requested",
    description: "The receiver was asked for the bank details needed to refund the deposit.",
  },
  {
    key: "BankDetailsSubmitted",
    label: "Bank Details Submitted",
    description: "Bank details were submitted and stored for the refund.",
  },
  {
    key: "Vacated",
    label: "Vacated",
    description: "Keys were handed back and the unit was confirmed vacated.",
  },
];

const RENTAL_STEPS: StepTemplate[] = [
  {
    key: "Listed",
    label: "Listed",
    description: "The apartment was published and is open to enquiries.",
  },
  {
    key: "Inquiry",
    label: "Inquiry Received",
    description: "The enquirer reached out about this apartment.",
  },
  {
    key: "AutoResponseSent",
    label: "Auto Response Email Sent",
    description: "An acknowledgement was emailed to the enquirer after they reached out.",
    workflow: "Optional",
  },
  {
    key: "TenantInfoRequested",
    label: "T&G Info Email Sent",
    description: "The tenant & guarantor information form was sent to the enquirer.",
    workflow: "Mandatory",
  },
  {
    key: "TenantInfoSubmitted",
    label: "T&G Info Submitted",
    description: "The enquirer submitted their tenant and guarantor details.",
  },
  {
    key: "ApplicationConfirmed",
    label: "Appfolio Application Confirmed",
    description: "Their submitted application was matched to the AppFolio record.",
  },
  {
    key: "PaymentConfirmed",
    label: "Venmo Payment Confirmed",
    description: "The holding payment was received and reconciled through Venmo.",
  },
  {
    key: "SlotsOffered",
    label: "Appointment Email Sent",
    description: "Viewing slots were offered to the enquirer.",
    workflow: "Mandatory",
  },
  {
    key: "TourBooked",
    label: "Tour Booked",
    description: "The enquirer confirmed a viewing slot.",
  },
  {
    key: TOURING_STEP,
    label: "Touring / Toured",
    // Stays active from the moment the slot starts through well after it ends (see
    // deriveTouring) — the label covers both states so it doesn't read as present-tense-only
    // once the visit is long over.
    description: "The group is touring, or has completed touring, the apartment at their confirmed slot.",
  },
  {
    key: "TourCancelled",
    label: "Tour Cancelled",
    description: "The confirmed viewing was cancelled, declined or rejected.",
  },
  {
    key: "Rented",
    label: "Apartment Marked as Rented",
    description: "The apartment is confirmed rented and removed from active marketing.",
  },
];

const FLOW_STEPS: Record<FlowKind, StepTemplate[]> = {
  Renewal: RENEWAL_STEPS,
  MoveOut: MOVEOUT_STEPS,
  Rental: RENTAL_STEPS,
};

/**
 * A group's display label. The tenancy has no real "name" of its own — it's just whoever's
 * occupants happen to be, a first-listed pick with no real meaning. An enquirer group does have
 * one: group.name is the lead prospect, the actual person who made contact.
 */
export function groupLabel(group: LifecycleGroup): string {
  return group.kind === "tenant" ? "Current Tenants" : group.name;
}

/** Fixed display order for the flow selector — Rental sits between the other two. */
export const ALL_FLOWS: FlowKind[] = ["Renewal", "Rental", "MoveOut"];

export const availableFlowsForGroup = (group: LifecycleGroup): FlowKind[] =>
  group.kind === "tenant" ? ["Renewal", "MoveOut"] : ["Rental"];

export function eventsForGroup(
  cycle: LifecycleCycle,
  group: LifecycleGroup,
): LifecycleEvent[] {
  if (group.kind === "prospect") {
    return cycle.events.filter(
      (e) =>
        e.flow === "Rental" &&
        (e.prospectOrTenantId
          ? e.prospectOrTenantId === group.id
          : e.eventType !== "Rented"),
    );
  }

  return cycle.events.filter((e) =>
    e.flow === "Rental" ? !e.prospectOrTenantId : true,
  );
}

/** The slot a group confirmed, from Rental.TourBooked, and whether a cancellation is still current. */
export function bookedSlot(events: LifecycleEvent[]) {
  const booked = events.find((e) => e.eventType === "TourBooked");
  const start = booked?.detail?.slotStart as string | undefined;
  const end = booked?.detail?.slotEnd as string | undefined;

  if (!start) return null;

  return {
    start,
    end,
    cancelled: isCurrentlyCancelled(events),
  };
}

/**
 * TourBooked and TourCancelled each merge into a single row per prospect (EmitAsync upserts on
 * cycle+eventType+prospect), so a tour can be cancelled and rebooked any number of times without
 * either event ever disappearing 
 */
function isCurrentlyCancelled(events: LifecycleEvent[]): boolean {
  const booked = events.find((e) => e.eventType === "TourBooked");
  const cancelled = events.find((e) => e.eventType === "TourCancelled");

  if (!cancelled) return false;
  if (!booked) return true;
  return cancelled.updatedAtUtc >= booked.updatedAtUtc;
}

export function formatSlot(start: string, end?: string): string {
  const slotStart = DateTime.fromISO(start);
  if (!slotStart.isValid) return "";

  const slotEnd = end ? DateTime.fromISO(end) : null;
  const from = slotStart.toFormat("MMM dd, yyyy h:mm a");

  return slotEnd?.isValid ? `${from} – ${slotEnd.toFormat("h:mm a")}` : from;
}

export function formatDay(value?: string): string {
  if (!value) return "Date TBD";

  // Event timestamps carry a Z and shift to the viewer's zone; date-only fields such as a lease
  // end carry no offset and are read as-is, so they never slide onto the previous day.
  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed.toFormat("MMM dd, yyyy") : "Date TBD";
}

/**
 * Turns a group's events into the flow's milestone list. Presence is the only signal — a
 * milestone with an event is active, everything else is inactive, and their stored order never
 * matters.
 */
export function buildSteps(
  flow: FlowKind,
  events: LifecycleEvent[],
  workflowType?: "Mandatory" | "Optional",
): TimelineStep[] {
  const flowEvents = events.filter((e) => e.flow === flow);
  const byType = new Map<string, LifecycleEvent[]>();

  flowEvents.forEach((e) => {
    byType.set(e.eventType, [...(byType.get(e.eventType) ?? []), e]);
  });

  const touring = flow === "Rental" ? deriveTouring(flowEvents) : null;

  const steps = FLOW_STEPS[flow]
    .filter(
      (t) =>
        !t.workflow ||
        !workflowType ||
        t.workflow === workflowType ||
        byType.has(t.key),
    )
    .map<TimelineStep>((template) => {
      if (template.key === TOURING_STEP) {
        return {
          key: template.key,
          label: template.label,
          description: template.description,
          active: !!touring,
          isLatest: false,
          occurredAt: touring?.occurredAt,
          // Derived straight from the confirmed slot time — always a real date, never a stand-in.
          dateExact: true,
          statusText: touring?.statusText ?? "Not started yet",
          facts: touring?.facts ?? [],
        };
      }

      const matches = byType.get(template.key) ?? [];

      if (template.key === "TourCancelled") {
        const current = isCurrentlyCancelled(flowEvents);
        return {
          key: template.key,
          label: template.label,
          description: template.description,
          workflow: template.workflow,
          active: current,
          isLatest: false,
          occurredAt: current ? matches[0]?.occurredAtUtc : undefined,
          dateExact: matches[0]?.detail?.dateExact !== false,
          statusText: current ? "Completed" : "Not started yet",
          facts: current ? matches.flatMap((e) => buildFacts(e.detail)) : [],
        };
      }

      return {
        key: template.key,
        label: template.label,
        description: template.description,
        workflow: template.workflow,
        active: matches.length > 0,
        isLatest: false,
        occurredAt: matches[0]?.occurredAtUtc,
        dateExact: matches[0]?.detail?.dateExact !== false,
        statusText: matches.length > 0 ? "Completed" : "Not started yet",
        facts: matches.flatMap((e) => buildFacts(e.detail)),
      };
    });

  const latest = steps
    .filter((s) => s.active && s.occurredAt)
    .sort((a, b) => (a.occurredAt! < b.occurredAt! ? 1 : -1))[0];

  if (latest) latest.isLatest = true;

  return steps;
}

/**
 * Touring becomes active once the confirmed slot has started, and reads as in progress until it
 * ends. A cancelled viewing never tours.
 */
function deriveTouring(rentalEvents: LifecycleEvent[]) {
  const slot = bookedSlot(rentalEvents);
  if (!slot || slot.cancelled) return null;

  const start = DateTime.fromISO(slot.start);
  if (!start.isValid || DateTime.local() < start) return null;

  const end = slot.end ? DateTime.fromISO(slot.end) : null;
  const finished = !end?.isValid || DateTime.local() > end;

  return {
    occurredAt: slot.start,
    statusText: finished ? "Completed" : "In progress",
    facts: [
      { label: "Slot", value: formatSlot(slot.start, slot.end), strong: true },
    ],
  };
}

const MONEY_KEYS = [
  "currentRent",
  "proposedRent",
  "agreedRent",
  "previousRent",
  "askingRent",
  "rentQuoted",
  "amount",
];

const DAY_KEYS = [
  "leaseStart",
  "leaseEnd",
  "newLeaseTo",
  "plannedMoveOutDate",
  "actualMoveOutDate",
  "targetMoveOut",
];

const SLOT_KEYS = ["slotStart", "slotEnd"];

const HIDDEN_KEYS = ["chosenReceiverId", "receiverId", "dateExact"];

const HUMANIZE_VALUE_KEYS = ["source", "scope", "paymentStatus"];

const FACT_LABELS: Record<string, string> = {
  currentRent: "Current rent",
  proposedRent: "Proposed rent",
  agreedRent: "Agreed rent",
  previousRent: "Previous rent",
  askingRent: "Asking rent",
  rentQuoted: "Rent quoted",
  leaseStart: "Lease start",
  leaseEnd: "Lease ends",
  newLeaseTo: "New lease to",
  plannedMoveOutDate: "Planned move-out",
  actualMoveOutDate: "Actual move-out",
  targetMoveOut: "Target move-out",
  slotStart: "Slot starts",
  slotEnd: "Slot ends",
  recipientCount: "Recipients",
  successCount: "Sent",
  failureCount: "Failed",
  recipients: "Recipients",
  groupSize: "Group size",
  tenantCount: "Tenants",
  votedBy: "Voted by",
  chosenReceiver: "Chose",
  accountHolder: "Account holder",
  accountNumber: "Account",
  routingCode: "IFSC",
  bankName: "Bank",
  threadId: "Email thread",
  cancelledBy: "Cancelled by",
  paymentStatus: "Payment status",
  sourceStatus: "Source status",
};

const STRONG_KEYS = [...MONEY_KEYS, ...SLOT_KEYS, "roster", "chosenReceiver", "receiver"];

export function buildFacts(
  detail?: Record<string, any> | null,
): TimelineFact[] {
  if (!detail) return [];

  return Object.entries(detail)
    .filter(([key, value]) => !HIDDEN_KEYS.includes(key) && !isEmpty(value))
    .map(([key, value]) => ({
      label: FACT_LABELS[key] ?? humanize(key),
      value: formatValue(key, value),
      strong: STRONG_KEYS.includes(key),
    }));
}

function isEmpty(value: any): boolean {
  if (value === null || value === undefined || value === "") return true;
  return Array.isArray(value) && value.length === 0;
}

function formatValue(key: string, value: any): string {
  if (Array.isArray(value)) return value.map(formatArrayItem).join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";

  if (MONEY_KEYS.includes(key) && typeof value === "number") {
    return `$${value.toLocaleString()}`;
  }

  if (DAY_KEYS.includes(key)) return formatDay(String(value));
  if (SLOT_KEYS.includes(key)) return formatSlot(String(value));
  if (HUMANIZE_VALUE_KEYS.includes(key) && typeof value === "string") {
    return humanizeValue(value);
  }

  return String(value);
}

function humanizeValue(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatArrayItem(item: any): string {
  if (item && typeof item === "object") {
    return String(item.name ?? "");
  }
  return String(item);
}

function humanize(key: string): string {
  const spaced = key.replace(/([A-Z])/g, " $1").toLowerCase().trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
